import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  MapPin, Pencil, Trash2, CheckCircle, Search, Loader2, Send,
  AlertTriangle, Truck, Tag, XCircle,
} from 'lucide-react';
import DeliveryZoneMap from '../components/map/DeliveryZoneMap';

// Public page bound to a lead's intake_token. No auth — token IS the auth.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// UI strings — the page renders in Spanish when the lead came from the
// Spanish order form (ctx.language === 'es', set by the backend).
const STRINGS = {
  en: {
    headerTagline: 'Draw your delivery zone — get a quote',
    receivedTitle: 'We received your order form',
    name: 'Name', email: 'Email', phone: 'Phone', business: 'Business',
    deliveryDate: 'Desired delivery date', quantity: 'Quantity',
    printNeeded: 'Print needed', designNeeded: 'Design needed',
    targetArea: 'Target area',
    couponApplied: (amt) => `$${amt} off applied at checkout`,
    couponLockedPre: 'Coupon',
    couponLockedPost: 'is locked in — finish drawing your map below and your invoice will be reduced automatically.',
    expiresLabel: 'Expires',
    couponInvalidTitle: (code) => `Coupon ${code} is no longer valid`,
    couponRedeemed: 'It looks like this coupon has already been used.',
    couponExpired: 'This coupon has expired. Don\'t worry — your map will still be reviewed for a custom quote.',
    couponFallback: 'You can still submit your map for a quote.',
    drawTitle: 'Draw your delivery zone',
    drawDesc: 'Use the map below to outline the neighborhood(s) you want flyers delivered in. Click to drop points, drag to fine-tune, click the green marker to close the shape.',
    searchPlaceholder: 'Search any address to center the map…',
    deliveryZone: 'Delivery Zone',
    drawZone: 'Draw Zone',
    drawingHint: 'Click on the map to place points. Click the green marker to close.',
    zoneDrawn: (n) => `Zone drawn (${n} points)`,
    clear: 'Clear',
    notesLabel: 'Anything else we should know?',
    optional: '(optional)',
    notesPlaceholder: 'Special instructions, neighborhoods to avoid, drop-off preferences…',
    estimating: 'Estimating houses…',
    estimatedHouses: 'estimated houses',
    approxNote: '(area-based approximation)',
    recalculate: 'Recalculate',
    estimateBtn: 'Estimate houses',
    submitBtn: 'Submit & get my quote',
    submittingBtn: 'Submitting…',
    successTitle: 'Thanks — we\'ve got your map!',
    successBody: 'Our team is reviewing your delivery zone and will email you a personalized quote shortly along with next steps.',
    successFooterPre: 'You can close this page. We\'ll be in touch at',
    questions: 'Questions? Email us at',
    toastAddressNotFound: 'Address not found',
    toastSearchFailed: 'Search failed',
    toastDraw3: 'Please draw a delivery zone with at least 3 points before submitting.',
    toastSubmitFailed: 'Submission failed — please try again.',
    toastDiscount: (amt) => `$${amt} discount locked in!`,
    toastCouponNotApplied: 'Submitted, but your coupon could not be applied (it may have expired).',
    toastEstimateFailed: 'Could not estimate house count right now — you can still submit your map.',
  },
  es: {
    headerTagline: 'Dibuje su zona de entrega — obtenga una cotización',
    receivedTitle: 'Recibimos su formulario de pedido',
    name: 'Nombre', email: 'Correo electrónico', phone: 'Teléfono', business: 'Empresa',
    deliveryDate: 'Fecha de entrega deseada', quantity: 'Cantidad',
    printNeeded: 'Impresión requerida', designNeeded: 'Diseño requerido',
    targetArea: 'Área objetivo',
    couponApplied: (amt) => `$${amt} de descuento aplicado al pagar`,
    couponLockedPre: 'El cupón',
    couponLockedPost: 'está asegurado — termine de dibujar su mapa abajo y su factura se reducirá automáticamente.',
    expiresLabel: 'Expira',
    couponInvalidTitle: (code) => `El cupón ${code} ya no es válido`,
    couponRedeemed: 'Parece que este cupón ya fue utilizado.',
    couponExpired: 'Este cupón ha expirado. No se preocupe — su mapa aún será revisado para una cotización personalizada.',
    couponFallback: 'Aún puede enviar su mapa para recibir una cotización.',
    drawTitle: 'Dibuje su zona de entrega',
    drawDesc: 'Use el mapa de abajo para delimitar el/los vecindario(s) donde desea que se entreguen los volantes. Haga clic para colocar puntos, arrastre para ajustar y haga clic en el marcador verde para cerrar la figura.',
    searchPlaceholder: 'Busque cualquier dirección para centrar el mapa…',
    deliveryZone: 'Zona de Entrega',
    drawZone: 'Dibujar Zona',
    drawingHint: 'Haga clic en el mapa para colocar puntos. Haga clic en el marcador verde para cerrar.',
    zoneDrawn: (n) => `Zona dibujada (${n} puntos)`,
    clear: 'Borrar',
    notesLabel: '¿Algo más que debamos saber?',
    optional: '(opcional)',
    notesPlaceholder: 'Instrucciones especiales, vecindarios a evitar, preferencias de entrega…',
    estimating: 'Estimando casas…',
    estimatedHouses: 'casas estimadas',
    approxNote: '(aproximación basada en área)',
    recalculate: 'Recalcular',
    estimateBtn: 'Estimar casas',
    submitBtn: 'Enviar y obtener mi cotización',
    submittingBtn: 'Enviando…',
    successTitle: '¡Gracias — recibimos su mapa!',
    successBody: 'Nuestro equipo está revisando su zona de entrega y le enviará por correo una cotización personalizada en breve, junto con los próximos pasos.',
    successFooterPre: 'Puede cerrar esta página. Nos pondremos en contacto en',
    questions: '¿Preguntas? Escríbanos a',
    toastAddressNotFound: 'Dirección no encontrada',
    toastSearchFailed: 'La búsqueda falló',
    toastDraw3: 'Por favor, dibuje una zona de entrega con al menos 3 puntos antes de enviar.',
    toastSubmitFailed: 'El envío falló — por favor, inténtelo de nuevo.',
    toastDiscount: (amt) => `¡Descuento de $${amt} asegurado!`,
    toastCouponNotApplied: 'Enviado, pero su cupón no pudo aplicarse (puede haber expirado).',
    toastEstimateFailed: 'No se pudo estimar el número de casas en este momento — aún puede enviar su mapa.',
  },
};

export default function LeadIntakePage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const couponFromUrl = (searchParams.get('coupon') || '').trim().toUpperCase();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [success, setSuccess] = useState(false);
  const t = STRINGS[ctx?.language === 'es' ? 'es' : 'en'];

  // Map drawing state — mirrors the existing ClientRequestJob component
  const [zonePoints, setZonePoints] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 29.7604, lng: -95.3698 }); // Houston default
  const [centerTrigger, setCenterTrigger] = useState(0);
  const [searchAddress, setSearchAddress] = useState('');
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');

  // Coupon state — populated when URL contains ?coupon=CODE and we
  // validate it against the public coupon endpoint. We show a banner
  // only when the coupon is genuinely valid (not expired/redeemed).
  const [coupon, setCoupon] = useState(null); // { code, discount, valid, expired, redeemed, message }

  const initialSearchSent = useRef(false);

  // Validate the URL coupon on mount (independent of the intake context
  // fetch — they can race).
  useEffect(() => {
    if (!couponFromUrl) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/coupon/${encodeURIComponent(couponFromUrl)}`);
        const d = res.data || {};
        setCoupon({
          code: d.code,
          discount: d.discount_amount,
          valid: !!d.valid,
          expired: !!d.expired,
          redeemed: !!d.redeemed,
          expires_at: d.expires_at,
        });
      } catch (e) {
        setCoupon({
          code: couponFromUrl,
          valid: false,
          message: e.response?.status === 404
            ? 'This coupon code was not recognized.'
            : 'We could not validate your coupon.',
        });
      }
    })();
  }, [couponFromUrl]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/public/marketing/intake/${token}`);
        setCtx(res.data);
        // If they previously submitted, show the success state on refresh
        // so a refreshed/shared link still confirms receipt instead of
        // re-opening the empty draw form.
        if (res.data.already_submitted) {
          setSuccess(true);
        }
        if (res.data.submitted_polygon && Array.isArray(res.data.submitted_polygon)) {
          setZonePoints(res.data.submitted_polygon);
        }
      } catch (e) {
        const status = e.response?.status;
        const detail = e.response?.data?.detail;
        if (status === 410) setError({ kind: 'expired', message: detail });
        else if (status === 404) setError({ kind: 'invalid', message: detail || 'Invalid intake link' });
        else setError({ kind: 'network', message: detail || 'Failed to load' });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Pre-center the map on the customer's target_area once the context loads.
  useEffect(() => {
    if (!ctx || initialSearchSent.current) return;
    if (ctx.target_area && ctx.target_area.length > 0) {
      initialSearchSent.current = true;
      (async () => {
        try {
          const res = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(ctx.target_area)}&key=${GOOGLE_MAPS_API_KEY}`,
          );
          const data = await res.json();
          if (data.results && data.results.length > 0) {
            const loc = data.results[0].geometry.location;
            setMapCenter({ lat: loc.lat, lng: loc.lng });
            setCenterTrigger(prev => prev + 1);
            setSearchAddress(data.results[0].formatted_address);
          }
        } catch { /* silent — user can still search manually */ }
      })();
    }
  }, [ctx]);

  // Map drawing handlers
  const handleAddPoint = useCallback((lat, lng) => {
    setZonePoints(prev => [...prev, [lat, lng]]);
  }, []);

  const handleDragPoint = useCallback((index, lat, lng) => {
    setZonePoints(prev => {
      const updated = [...prev];
      updated[index] = [lat, lng];
      return updated;
    });
  }, []);

  const handleInsertPoint = useCallback((afterIndex, position) => {
    setZonePoints(prev => {
      const updated = [...prev];
      updated.splice(afterIndex + 1, 0, [position.lat, position.lng]);
      return updated;
    });
  }, []);

  const handleClosePolygon = useCallback(() => {
    if (zonePoints.length >= 3) setIsDrawing(false);
  }, [zonePoints.length]);

  const handleStartDrawing = () => {
    setIsDrawing(true);
    setZonePoints([]);
  };
  const handleClearZone = () => {
    setIsDrawing(false);
    setZonePoints([]);
  };

  const handleSearchAddress = async () => {
    if (!searchAddress.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(searchAddress)}&key=${GOOGLE_MAPS_API_KEY}`,
      );
      const data = await res.json();
      if (data.results && data.results.length > 0) {
        const loc = data.results[0].geometry.location;
        setMapCenter({ lat: loc.lat, lng: loc.lng });
        setCenterTrigger(prev => prev + 1);
        setSearchAddress(data.results[0].formatted_address);
      } else {
        toast.error(t.toastAddressNotFound);
      }
    } catch {
      toast.error(t.toastSearchFailed);
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (zonePoints.length < 3) {
      toast.error(t.toastDraw3);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/marketing/intake/${token}/submit`, {
        polygon: zonePoints,
        location_text: searchAddress,
        notes,
        // Only pass the coupon when it's actually valid — keeps the
        // backend's audit log clean and prevents a stale/expired code
        // from confusing the redemption path.
        coupon_code: (coupon && coupon.valid && coupon.code) ? coupon.code : undefined,
      });
      if (res.data?.applied_coupon_code) {
        toast.success(t.toastDiscount(res.data.applied_coupon_amount?.toFixed(2) || '100.00'));
      } else if (res.data?.coupon_error && coupon?.code) {
        toast.warning(t.toastCouponNotApplied);
      }
      setSuccess(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || t.toastSubmitFailed);
    } finally {
      setSubmitting(false);
    }
  };

  // ----- House count estimate ---------------------------------------
  // We deliberately do NOT live-update the count as the polygon is
  // edited — the user asked for the count to appear only AFTER they
  // finish drawing. Showing it during drawing causes janky re-counts
  // and exposes parcel queries on every drag. Instead, when the
  // polygon flips from "open" (<3 pts) → "closed" (>=3 pts), we
  // fire ONE estimate call and stash the result. Any subsequent
  // edit invalidates the count and a Recalculate button shows.
  const [houseEstimate, setHouseEstimate] = useState(null); // { count, confidence, approximated } | null
  const [estimating, setEstimating] = useState(false);
  const [estimateStale, setEstimateStale] = useState(false);

  const runEstimate = useCallback(async () => {
    if (zonePoints.length < 3) return;
    setEstimating(true);
    setEstimateStale(false);
    try {
      const res = await axios.post(
        `${API}/public/marketing/intake/${token}/estimate-houses`,
        { polygon: zonePoints },
      );
      setHouseEstimate(res.data || null);
    } catch (e) {
      // Soft-fail: estimate is informational, submission still works
      setHouseEstimate(null);
      toast.error('Could not estimate house count right now — you can still submit your map.');
    } finally {
      setEstimating(false);
    }
  }, [token, zonePoints]);

  // First-fire trigger: when the polygon becomes closeable (3+ pts)
  // and we don't have an estimate yet, run once. Subsequent drags or
  // adds mark the existing count "stale" — the user clicks
  // Recalculate when they're ready.
  const hasClosablePolygon = zonePoints.length >= 3;
  const polygonSignature = JSON.stringify(zonePoints);
  const lastEstimateSig = useRef(null);
  useEffect(() => {
    if (!hasClosablePolygon) {
      setHouseEstimate(null);
      setEstimateStale(false);
      lastEstimateSig.current = null;
      return;
    }
    if (houseEstimate === null && lastEstimateSig.current !== polygonSignature) {
      // First time we have a closeable polygon → fire once.
      lastEstimateSig.current = polygonSignature;
      runEstimate();
    } else if (houseEstimate !== null && lastEstimateSig.current !== polygonSignature) {
      // Polygon changed after a previous estimate → flag stale.
      setEstimateStale(true);
    }
  }, [hasClosablePolygon, polygonSignature, houseEstimate, runEstimate]);

  const handleRecalculate = () => {
    lastEstimateSig.current = polygonSignature;
    runEstimate();
  };

  const hasZone = zonePoints.length >= 3 && !isDrawing;

  // ============== Render branches ==============

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="intake-loading">
        <Loader2 className="animate-spin text-purple-500" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="intake-error">
        <Card className="max-w-md p-8 text-center">
          <AlertTriangle className="text-amber-500 mx-auto mb-3" size={42} />
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {error.kind === 'expired' ? 'This link has expired' : 'We couldn\'t find your link'}
          </h1>
          <p className="text-sm text-gray-600 mb-4">{error.message}</p>
          <p className="text-xs text-gray-500">
            Email us at <a href="mailto:info@localflyerdelivery.com" className="text-purple-600 underline">info@localflyerdelivery.com</a> and we&apos;ll send a fresh link.
          </p>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="intake-success">
        <Card className="max-w-lg p-10 text-center">
          <CheckCircle className="text-emerald-500 mx-auto mb-3" size={56} />
          <h1 className="text-2xl font-bold text-gray-900 mb-3">{t.successTitle}</h1>
          <p className="text-gray-700 mb-2">
            {t.successBody}
          </p>
          <p className="text-sm text-gray-500 mt-6">
            {t.successFooterPre} <b>{ctx?.email}</b>.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="lead-intake-page">
      {/* Branded header */}
      <header className="bg-gradient-to-br from-purple-600 to-purple-800 text-white py-6 px-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Truck size={28} />
          <div>
            <h1 className="text-xl font-bold">Local Flyer Delivery</h1>
            <p className="text-sm text-purple-200">{t.headerTagline}</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Pre-fill summary card */}
        <Card className="p-5" data-testid="intake-context-card">
          <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
            <CheckCircle className="text-emerald-500" size={18} />
            {t.receivedTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <SummaryRow label={t.name} value={ctx?.name} />
            <SummaryRow label={t.email} value={ctx?.email} />
            <SummaryRow label={t.phone} value={ctx?.phone} />
            <SummaryRow label={t.business} value={ctx?.business_name} />
            <SummaryRow label={t.deliveryDate} value={ctx?.delivery_date} />
            <SummaryRow label={t.quantity} value={ctx?.delivery_quantity} />
            <SummaryRow label={t.printNeeded} value={ctx?.print_needed} />
            <SummaryRow label={t.designNeeded} value={ctx?.design_needed} />
          </div>
          {ctx?.target_area && (
            <div className="mt-3 pt-3 border-t">
              <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">{t.targetArea}</p>
              <p className="text-sm text-gray-800">{ctx.target_area}</p>
            </div>
          )}
        </Card>

        {/* Coupon banner — only renders when ?coupon=CODE is on the URL.
            Three states: valid (green), expired/redeemed (amber), unknown (red). */}
        {coupon && (
          <div data-testid="intake-coupon-banner">
            {coupon.valid ? (
              <div className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-emerald-100/60 p-5 flex items-start gap-4 shadow-sm">
                <div className="rounded-full bg-emerald-500 text-white p-2.5 shrink-0">
                  <Tag size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-emerald-900">
                    {t.couponApplied(Number(coupon.discount || 100).toFixed(0))}
                  </p>
                  <p className="text-sm text-emerald-800 mt-0.5">
                    {t.couponLockedPre} <span className="font-mono font-semibold">{coupon.code}</span> {t.couponLockedPost}
                  </p>
                  {coupon.expires_at && (
                    <p className="text-xs text-emerald-700 mt-1">
                      {t.expiresLabel} {new Date(coupon.expires_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
                   data-testid="intake-coupon-invalid">
                <XCircle className="text-amber-600 shrink-0 mt-0.5" size={18} />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold">{t.couponInvalidTitle(coupon.code)}</p>
                  <p className="text-amber-800 mt-0.5">
                    {coupon.redeemed
                      ? t.couponRedeemed
                      : coupon.expired
                        ? t.couponExpired
                        : (coupon.message || t.couponFallback)}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">{t.drawTitle}</h2>
              <p className="text-sm text-gray-500">
                {t.drawDesc}
              </p>
            </div>

            {/* Address search to recenter */}
            <div className="flex gap-2">
              <Input
                value={searchAddress}
                onChange={e => setSearchAddress(e.target.value)}
                placeholder={t.searchPlaceholder}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSearchAddress(); } }}
                className="flex-1"
                data-testid="intake-map-search"
              />
              <Button type="button" size="sm" onClick={handleSearchAddress} disabled={searching} variant="outline" data-testid="intake-map-search-btn">
                {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              </Button>
            </div>

            {/* Drawing controls */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="text-sm font-semibold flex items-center gap-1.5">
                <MapPin size={15} className="text-purple-500" />
                {t.deliveryZone}
              </Label>
              <div className="flex items-center gap-2">
                {!isDrawing && zonePoints.length === 0 && (
                  <Button type="button" size="sm" onClick={handleStartDrawing} className="bg-purple-600 hover:bg-purple-500 text-white" data-testid="intake-start-drawing">
                    <Pencil size={14} className="mr-1" /> {t.drawZone}
                  </Button>
                )}
                {isDrawing && (
                  <p className="text-xs text-purple-600 font-medium animate-pulse">
                    {t.drawingHint}
                  </p>
                )}
                {hasZone && (
                  <>
                    <Badge className="bg-green-100 text-green-700 text-xs">
                      <CheckCircle size={12} className="mr-1" /> {t.zoneDrawn(zonePoints.length)}
                    </Badge>
                    <Button type="button" size="sm" variant="outline" onClick={handleClearZone} className="text-red-500 border-red-200 hover:bg-red-50" data-testid="intake-clear-zone">
                      <Trash2 size={14} className="mr-1" /> {t.clear}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Map */}
            <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: 460 }} data-testid="intake-zone-map">
              <DeliveryZoneMap
                initialCenter={mapCenter}
                initialZoom={14}
                isDrawing={isDrawing}
                zonePoints={zonePoints}
                onAddPoint={handleAddPoint}
                onDragPoint={handleDragPoint}
                onInsertPoint={handleInsertPoint}
                onClosePolygon={handleClosePolygon}
                centerTrigger={centerTrigger}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-sm">{t.notesLabel} <span className="text-gray-400 font-normal">{t.optional}</span></Label>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder={t.notesPlaceholder}
                rows={3}
                data-testid="intake-notes"
              />
            </div>

            <div className="pt-2 flex flex-wrap items-center justify-between gap-3">
              {/* Estimated house count — only renders once the polygon
                  has been closed (>=3 points). Shows a Recalculate
                  affordance when the polygon has been edited since
                  the last count. */}
              {hasClosablePolygon && (
                <div
                  className="flex items-center gap-3 text-sm bg-purple-50 border border-purple-200 rounded-md px-3 py-2"
                  data-testid="intake-house-estimate"
                >
                  {estimating ? (
                    <>
                      <Loader2 size={14} className="animate-spin text-purple-600" />
                      <span className="text-purple-800">{t.estimating}</span>
                    </>
                  ) : houseEstimate && houseEstimate.count > 0 ? (
                    <>
                      <Truck size={14} className="text-purple-600" />
                      <span className="text-purple-900 font-semibold">
                        {houseEstimate.approximated ? '≈ ' : ''}
                        {houseEstimate.count.toLocaleString()} {t.estimatedHouses}
                      </span>
                      {houseEstimate.approximated && (
                        <span className="text-[11px] text-purple-700 italic">
                          {t.approxNote}
                        </span>
                      )}
                      {estimateStale && (
                        <button
                          type="button"
                          onClick={handleRecalculate}
                          className="text-[11px] text-purple-700 hover:text-purple-900 underline"
                          data-testid="intake-recalculate"
                        >
                          {t.recalculate}
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={handleRecalculate}
                      className="text-purple-700 hover:text-purple-900 font-medium text-sm"
                      data-testid="intake-estimate-trigger"
                    >
                      <Truck size={14} className="inline mr-1" /> {t.estimateBtn}
                    </button>
                  )}
                </div>
              )}
              <Button
                type="submit"
                disabled={submitting || !hasZone}
                className="bg-purple-600 hover:bg-purple-500 ml-auto"
                data-testid="intake-submit"
              >
                {submitting
                  ? <><Loader2 size={16} className="mr-2 animate-spin" /> {t.submittingBtn}</>
                  : <><Send size={16} className="mr-2" /> {t.submitBtn}</>
                }
              </Button>
            </div>
          </form>
        </Card>

        <p className="text-xs text-gray-500 text-center">
          {t.questions} <a href="mailto:info@localflyerdelivery.com" className="text-purple-600 hover:underline">info@localflyerdelivery.com</a>
        </p>
      </main>
    </div>
  );
}

const SummaryRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-0.5">{label}</p>
      <p className="text-sm text-gray-800">{value}</p>
    </div>
  );
};
